export interface EmailJSParams {
  to_name: string;
  to_email: string;
  from_name?: string;
  subject: string;
  message: string;
  reply_to?: string;
  [key: string]: any;
}

export function stripHtml(html: string): string {
  if (!html) return '';
  // Replace common block/formatting tags with newlines or spaces to preserve readability
  let text = html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<\/li>/gi, '\n')
    .replace(/<\/tr>/gi, '\n')
    .replace(/<\/h[1-6]>/gi, '\n\n')
    .replace(/<\/td>/gi, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");

  // Remove all remaining HTML tags
  text = text.replace(/<[^>]*>?/gm, '');

  // Compress multiple newlines
  text = text.replace(/\n{3,}/g, '\n\n');

  return text.trim();
}

/**
 * Sends a real email using EmailJS REST API if credentials are provided in .env.
 * If credentials are not present, it will log a warning to the console.
 */
export async function sendRealEmail(params: EmailJSParams): Promise<boolean> {
  const serviceId = import.meta.env.VITE_EMAILJS_SERVICE_ID;
  const templateId = import.meta.env.VITE_EMAILJS_TEMPLATE_ID;
  const publicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY;

  if (!serviceId || !templateId || !publicKey) {
    console.warn(
      'EmailJS credentials are not configured in your .env file.\n' +
      'Please add VITE_EMAILJS_SERVICE_ID, VITE_EMAILJS_TEMPLATE_ID, and VITE_EMAILJS_PUBLIC_KEY to enable real email sending.'
    );
    return false;
  }

  try {
    const { to_name, to_email, from_name, subject, message, reply_to, ...rest } = params;
    const cleanMessage = stripHtml(message);
    const cleanSubject = stripHtml(subject);

    const payload = {
      service_id: serviceId,
      template_id: templateId,
      user_id: publicKey,
      template_params: {
        to_name,
        to_email,
        from_name: from_name || 'SensorZone.lk',
        subject: cleanSubject,
        message: cleanMessage,
        reply_to: reply_to || 'sensorzonetronic@gmail.com',
        ...rest
      }
    };

    const response = await fetch('https://api.emailjs.com/api/v1.0/email/send', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (response.ok) {
      console.log(`Email successfully dispatched via EmailJS to ${params.to_email}!`);
      return true;
    } else {
      const errorText = await response.text();
      console.error('EmailJS API responded with error:', errorText);
      return false;
    }
  } catch (error) {
    console.error('Failed to dispatch email via EmailJS:', error);
    return false;
  }
}
